import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { authApi, type CurrentUser, type LoginRequest, type RegisterRequest } from '@/features/auth/api'
import { configureAuthExpiredHandler } from '@/shared/api/http'
import { isApiError } from '@/shared/api/problem-details'

type SessionState = 'unknown' | 'loading' | 'authenticated' | 'anonymous'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const state = ref<SessionState>('unknown')
  let hydratePromise: Promise<void> | undefined

  const isAuthenticated = computed(() => state.value === 'authenticated' && user.value !== null)
  const isSystemAdmin = computed(() => user.value?.roles.includes('SystemAdmin') ?? false)

  function clearSession(): void {
    user.value = null
    state.value = 'anonymous'
  }

  configureAuthExpiredHandler(clearSession)

  async function hydrate(): Promise<void> {
    if (state.value === 'authenticated' || state.value === 'anonymous') return
    if (hydratePromise) return hydratePromise

    state.value = 'loading'
    hydratePromise = (async () => {
      try {
        user.value = await authApi.me()
        state.value = 'authenticated'
      } catch (error) {
        if (isApiError(error) && error.status === 401) {
          clearSession()
          return
        }
        state.value = 'unknown'
        throw error
      } finally {
        hydratePromise = undefined
      }
    })()

    return hydratePromise
  }

  async function login(request: LoginRequest): Promise<void> {
    const responseUser = await authApi.login(request)
    user.value = responseUser ?? (await authApi.me())
    state.value = 'authenticated'
  }

  async function register(request: RegisterRequest): Promise<void> {
    const responseUser = await authApi.register(request)
    user.value = responseUser ?? (await authApi.me())
    state.value = 'authenticated'
  }

  async function logout(): Promise<void> {
    await authApi.logout()
    clearSession()
  }

  return {
    user,
    state,
    isAuthenticated,
    isSystemAdmin,
    hydrate,
    login,
    register,
    logout,
    clearSession,
  }
})
